import * as React from "react";
import { FieldProps } from "formik";
import { Form, Select } from "antd";

import searchIcon from "../../assets/discovery/search-icon.png";

const FormItem = Form.Item;

type SearchableOption = {
  key?: string;
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
};

const getTextContent = (value: React.ReactNode): string => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map(getTextContent).join(" ");
  }

  if (React.isValidElement(value)) {
    const element = value as React.ReactElement<{ children?: React.ReactNode }>;
    return getTextContent(element.props.children);
  }

  return "";
};

const getFilteredChildren = (
  children: React.ReactNode,
  searchTerm: string
): React.ReactNode[] => {
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  return React.Children.toArray(children).filter((child) => {
    if (!React.isValidElement(child)) {
      return false;
    }

    if (!normalizedSearchTerm) {
      return true;
    }

    const childProps = child.props as {
      children?: React.ReactNode;
      value?: string;
    };

    const optionText = `${getTextContent(childProps.children)} ${childProps.value || ""}`
      .trim()
      .toLowerCase();

    return optionText.includes(normalizedSearchTerm);
  });
};

export const SearchableSelectField: React.FC<
  FieldProps<any> & {
    label?: string;
    className?: string;
    placeholder?: string;
    options?: SearchableOption[];
    children?: React.ReactNode;
    searchPlaceholder?: string;
    emptyMessage?: string;
    onValueChange?: (newValue: any) => void;
  }
> = ({
  field: { onChange, ...field },
  form: { touched, errors, submitCount, setFieldValue, setFieldTouched },
  label,
  className = "bg-[#171820] [&_.ant-select-selection-placeholder]:!text-white [&_.ant-select-selection-placeholder]:!opacity-80 [&_.ant-select-selector]:!text-white",
  placeholder,
  options,
  children,
  searchPlaceholder = "Search",
  emptyMessage = "No options found",
  onValueChange,
  ...props
}) => {
  const [searchTerm, setSearchTerm] = React.useState("");

  const shouldShowError = Boolean(touched[field.name] || submitCount > 0);
  const rawError = shouldShowError ? errors[field.name] : undefined;
  const errorMsg =
    typeof rawError === "string"
      ? rawError
      : Array.isArray(rawError)
      ? rawError.join(", ")
      : undefined;

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  const filteredOptions = options
    ? options.filter((option) => {
        if (!normalizedSearchTerm) {
          return true;
        }

        const optionText = `${getTextContent(option.label)} ${option.value}`
          .trim()
          .toLowerCase();

        return optionText.includes(normalizedSearchTerm);
      })
    : undefined;

  const filteredChildren = options
    ? null
    : getFilteredChildren(children, searchTerm);

  return (
    <FormItem
      label={label}
      help={errorMsg}
      validateStatus={errorMsg ? "error" : undefined}
    >
      <Select
        {...field}
        {...props}
        value={field.value || undefined}
        className={className}
        placeholder={placeholder}
        filterOption={false}
        popupClassName="searchable-select-dropdown"
        popupRender={(menu) => (
          <div className="searchable-select-popup">
            <div className="px-2 pt-2 pb-1 bg-[#1E1E1E]">
              <div
                className="flex gap-3 items-center w-full px-4 py-3 bg-[#404040] rounded-md shadow-sm"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
              >
                <img src={searchIcon} alt="search" width={20} height={20} />
                <input
                  autoFocus
                  type="search"
                  value={searchTerm}
                  placeholder={searchPlaceholder}
                  className="border-0 !bg-[#404040] text-white outline-none w-full placeholder:text-white placeholder:text-[14px]"
                  onChange={(event) => setSearchTerm(event.target.value)}
                  onKeyDown={(event) => event.stopPropagation()}
                />
              </div>
            </div>
            {menu}
          </div>
        )}
        virtual={false}
        onChange={(newValue: any) => {
          setFieldValue(field.name, newValue);
          onValueChange?.(newValue);
        }}
        onBlur={() => setFieldTouched(field.name, true)}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setSearchTerm("");
          }
        }}
        notFoundContent={
          <div className="px-3 py-2 text-sm text-white/60">{emptyMessage}</div>
        }
        {...(filteredOptions && { options: filteredOptions })}
      >
        {!options && filteredChildren}
      </Select>
    </FormItem>
  );
};
